import type { ComponentChildren } from "preact";
import { useState } from "preact/hooks";
import { t } from "../i18n/index.ts";
import { slide, until } from "../lib/shift.ts";
import { Button } from "./Button.tsx";
import { type Fitted, useFit } from "./fit.ts";
import { NextIcon, PreviousIcon } from "./icons.tsx";
import { PageJump } from "./PageJump.tsx";

/** How many page numbers show on each side of the current one (the first and last always do), room allowing. */
const AROUND = 2;

/** Page numbers that may show: the first, the last, and `AROUND` on each side of the current one. */
function numbers(current: number, last: number): number[] {
  const out: number[] = [];
  for (let n = 1; n <= last; n++) {
    if (n === 1 || n === last || Math.abs(n - current) <= AROUND) {
      out.push(n);
    }
  }
  return out;
}

/**
 * How soon each goes when the pager's too narrow (`useFit`'s `drop`: higher first): the numbers farthest from the
 * current one, then the nearer ones, then the first and last, then where it is ("51–100 of 345"). The current one,
 * Previous and Next stay.
 */
function dropOf(n: number, current: number, last: number): number | undefined {
  if (n === current) {
    return undefined;
  }
  return n === 1 || n === last ? EDGE_DROP : NEAR_DROP + Math.abs(n - current);
}
const RANGE_DROP = 1;
/** Narrow, Previous and Next a chevron alone, their word there for its name (no room for it seen). */
const CHEVRON_ONLY = "max-sm:gap-0 max-sm:text-[0px]";
const EDGE_DROP = 2;
const NEAR_DROP = 3;

/** The numbers shown, with gaps (null) where some aren't: 1 … 4 5 [6] 7 8 … 20. */
function withGaps(shown: readonly number[]): (number | null)[] {
  const out: (number | null)[] = [];
  for (const n of shown) {
    const before = out.at(-1);
    if (typeof before === "number" && n > before + 1) {
      out.push(null);
    }
    out.push(n);
  }
  return out;
}

/** The longest another page is waited for in a view transition (the screen held meanwhile): on, as it is, after. */
const WAIT_MS = 600;

/** The list a pager's for: what's between it and the other one (`Paged`: after the one above, before the one below),
 *  or after it. */
function listOf(nav: Element | null): HTMLElement | null {
  const next = nav?.nextElementSibling;
  const before = nav?.previousElementSibling;
  const list =
    before?.previousElementSibling?.matches("[data-pager]") && !next?.nextElementSibling?.matches("[data-pager]")
      ? before
      : next;
  return list instanceof HTMLElement && !list.matches("[data-pager]") ? list : null;
}

/** The pagers on the page, in order. */
const pagers = () => [...document.querySelectorAll<HTMLElement>("[data-pager]")];

/**
 * Each pager's buttons named in a view transition (or not), after where it is among the page's (its own apart from
 * another's, whatever was drawn again): a page's number moves to where it is then, Previous and Next stay.
 */
function nameButtons(on: boolean): void {
  pagers().forEach((nav, i) => {
    for (const b of nav.querySelectorAll<HTMLElement>("[data-to]")) {
      b.style.viewTransitionName = on ? `pager-${i}-${b.dataset["to"]}` : "";
    }
  });
}

/**
 * Where a page of a counted list is ("51–100 of 345") and a way to the others: previous, numbered pages, next. On
 * one line: as many numbers as fit (`dropOf`); narrow, Previous and Next at its ends (put, whatever's between them).
 * The current page's number, tapped, asks for another to go to. Nothing when it all fits in one page. `go` gets the cursor of the page to show (null: the first). In the iOS app,
 * another page is a view transition: the list sliding to it (once it's there), each pager's numbers moving to where
 * they are then (`nameButtons`).
 */
export function Pager(p: {
  page: { total?: number | null; offset?: number | null; data: readonly unknown[] };
  size: number;
  go: (cursor: string | null) => void;
  class?: string;
}) {
  const total = p.page.total ?? 0;
  const offset = p.page.offset ?? 0;
  const last = Math.max(1, Math.ceil(total / p.size));
  const current = Math.floor(offset / p.size) + 1;
  const all = numbers(current, last);
  const items: Fitted[] = [
    { key: "range", drop: RANGE_DROP },
    { key: "previous" },
    ...all.map((n) => ({ key: `n${n}`, drop: dropOf(n, current, last) })),
    { key: "next" },
  ];
  // On one line: what doesn't fit isn't shown (fit.ts).
  const { box, fit } = useFit(items, { guess: 0 });
  // The current page's number, tapped: "Go to page" (from this pager).
  const [jumping, setJumping] = useState<Element | null>(null);
  if (total <= p.size && offset === 0) {
    return null;
  }
  const to = (n: number, from: Element) => {
    const at = pagers().indexOf(from.closest<HTMLElement>("[data-pager]") as HTMLElement);
    slide({
      change: () => p.go(n <= 1 ? null : String((n - 1) * p.size)),
      // The list now, and the list then (its pager drawn again: found again where it was).
      find: () => listOf(pagers()[at] ?? null),
      back: n < current,
      // Once the page asked for is the one shown (its data's here).
      ready: () => until(() => Boolean(pagers()[at]?.querySelector(`[aria-current="page"][data-to="n${n}"]`)), WAIT_MS),
      name: nameButtons,
    });
  };
  const shown = (key: string) => !fit.more.has(key);
  return (
    <nav
      ref={box as { current: HTMLElement | null }}
      aria-label={t("pager.label")}
      data-pager=""
      class={`relative flex flex-nowrap items-center gap-1 ${p.class ?? ""}`}
    >
      {shown("range") ? (
        <span
          data-bar-item="range"
          class="mr-auto pr-1 text-sm whitespace-nowrap text-zinc-500 tabular-nums max-sm:mr-0 max-sm:ml-auto"
        >
          {t("pager.range", { from: offset + 1, to: offset + p.page.data.length, total })}
        </span>
      ) : null}
      <Button
        size="sm"
        disabled={current <= 1}
        data-bar-item="previous"
        data-to="previous"
        class={`ml-auto max-sm:order-first max-sm:ml-0 ${CHEVRON_ONLY}`}
        onClick={(e) => to(current - 1, e.currentTarget)}
      >
        <PreviousIcon size={16} />
        {t("pager.previous")}
      </Button>
      {withGaps(all.filter((n) => shown(`n${n}`))).map((n, i) =>
        n === null ? (
          // biome-ignore lint/suspicious/noArrayIndexKey: gaps have nothing else to tell them apart.
          <span key={`gap${i}`} class="px-1 text-zinc-400">
            …
          </span>
        ) : (
          <Button
            key={n}
            size="sm"
            variant={n === current ? "primary" : "secondary"}
            aria-current={n === current ? "page" : undefined}
            aria-label={n === current ? t("pager.current", { n }) : undefined}
            data-bar-item={`n${n}`}
            data-to={`n${n}`}
            // Narrow, Previous and Next at the ends, staying put: what's between them centred (the first of it pushed).
            class={i === 0 && !shown("range") ? "max-sm:ml-auto" : undefined}
            onClick={(e) => (n === current ? setJumping(e.currentTarget) : to(n, e.currentTarget))}
          >
            <span class="tabular-nums">{n}</span>
          </Button>
        ),
      )}
      <Button
        size="sm"
        disabled={current >= last}
        data-bar-item="next"
        data-to="next"
        class={`max-sm:ml-auto ${CHEVRON_ONLY}`}
        onClick={(e) => to(current + 1, e.currentTarget)}
      >
        {t("pager.next")}
        <NextIcon size={16} />
      </Button>
      <PageJump
        open={jumping !== null}
        current={current}
        last={last}
        go={(n) => (jumping ? to(n, jumping) : undefined)}
        onClose={() => setJumping(null)}
      />
      {/* The room the gaps take once numbers aren't shown (two at most, with theirs): measured, not shown. */}
      <span data-bar-more="" aria-hidden="true" class="invisible absolute flex gap-1 whitespace-nowrap">
        <span class="px-1">…</span>
        <span class="px-1">…</span>
      </span>
    </nav>
  );
}

/**
 * A page of a counted list between two pagers, one above it and one below (`above` and `below` their spacing; the
 * list's own by default).
 */
export function Paged(p: {
  page: { total?: number | null; offset?: number | null; data: readonly unknown[] };
  size: number;
  go: (cursor: string | null) => void;
  above?: string;
  below?: string;
  children: ComponentChildren;
}) {
  return (
    <>
      <Pager page={p.page} size={p.size} go={p.go} class={p.above ?? "mb-3"} />
      {p.children}
      <Pager page={p.page} size={p.size} go={p.go} class={p.below ?? "mt-4"} />
    </>
  );
}
